import { useState, useEffect, useCallback } from 'react';
import { sortByName } from '../lib/sortByName';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format, formatDistanceToNow } from 'date-fns';
import { useAuth } from '../lib/auth';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
    Shield, Search, RefreshCw, Download, Filter, ChevronLeft, ChevronRight,
    DollarSign, Gift, Users, TrendingUp, Eye, Clock, FileText
} from 'lucide-react';
import { parseUtc } from '../lib/datetime';

const STRIPE_BADGE = {
    red: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
    green: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
    gold: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
    blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
};

const STATUS_BADGE = {
    active: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
    redeemed: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
    expired: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
    voided: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
};

export default function ManagementAudit() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { hasPermission, isSuperAdmin } = useAuth();
    const [activeTab, setActiveTab] = useState('vouchers');

    // Voucher Activity
    const [vouchers, setVouchers] = useState([]);
    const [vTotal, setVTotal] = useState(0);
    const [vLoading, setVLoading] = useState(true);
    const [vPage, setVPage] = useState(0);
    const vLimit = 50;

    // Filters
    const [fColor, setFColor] = useState('all');
    const [fReason, setFReason] = useState('all');
    const [fStatus, setFStatus] = useState('all');
    const [fStartDate, setFStartDate] = useState('');
    const [fEndDate, setFEndDate] = useState('');
    const [fSearch, setFSearch] = useState('');
    const [fLocation, setFLocation] = useState('all');

    // Staff Incentives
    const [staffList, setStaffList] = useState([]);
    const [sLoading, setSLoading] = useState(false);

    // Detail modal
    const [detailVoucher, setDetailVoucher] = useState(null);
    const [detailAudit, setDetailAudit] = useState([]);
    const [detailOpen, setDetailOpen] = useState(false);

    // Locations
    const [locations, setLocations] = useState([]);

    useEffect(() => {
        if (!hasPermission('view_management_audit')) { navigate('/'); return; }
        loadLocations();
    }, []);

    useEffect(() => { if (activeTab === 'vouchers') fetchVouchers(); }, [vPage, fColor, fReason, fStatus, fStartDate, fEndDate, fLocation, activeTab]);
    useEffect(() => { if (activeTab === 'incentives') fetchStaffIncentives(); }, [activeTab]);

    const loadLocations = async () => {
        try { const r = await api.get('/locations'); setLocations(sortByName(r.data)); } catch {}
    };

    const fetchVouchers = useCallback(async () => {
        setVLoading(true);
        try {
            const p = { limit: vLimit, skip: vPage * vLimit };
            if (fColor !== 'all') p.stripe_color = fColor;
            if (fReason !== 'all') p.reason_code = fReason;
            if (fStatus !== 'all') p.status = fStatus;
            if (fLocation !== 'all') p.location_id = fLocation;
            if (fStartDate) p.start_date = fStartDate;
            if (fEndDate) p.end_date = fEndDate;
            const r = await api.get('/management/voucher-activity', { params: p });
            setVouchers(r.data.vouchers || []);
            setVTotal(r.data.total || 0);
        } catch { toast.error('Failed to load voucher activity'); }
        setVLoading(false);
    }, [vPage, fColor, fReason, fStatus, fStartDate, fEndDate, fLocation]);

    const fetchStaffIncentives = async () => {
        setSLoading(true);
        try {
            const r = await api.get('/management/staff-incentives');
            setStaffList(r.data.staff || []);
        } catch { toast.error('Failed to load staff incentives'); }
        setSLoading(false);
    };

    const openDetail = async (code) => {
        try {
            const r = await api.get(`/management/vouchers/${code}/detail`);
            setDetailVoucher(r.data.voucher);
            setDetailAudit(r.data.audit_trail || []);
            setDetailOpen(true);
        } catch { toast.error('Failed to load voucher detail'); }
    };

    const handleExport = async () => {
        try {
            const p = {};
            if (fColor !== 'all') p.stripe_color = fColor;
            if (fReason !== 'all') p.reason_code = fReason;
            if (fStatus !== 'all') p.status = fStatus;
            if (fLocation !== 'all') p.location_id = fLocation;
            if (fStartDate) p.start_date = fStartDate;
            if (fEndDate) p.end_date = fEndDate;
            const r = await api.get('/management/voucher-activity/export', { params: p, responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([r.data]));
            const a = document.createElement('a');
            a.href = url; a.download = `voucher-activity-${new Date().toISOString().split('T')[0]}.csv`;
            a.click(); window.URL.revokeObjectURL(url);
            toast.success('Export complete');
        } catch { toast.error('Export failed'); }
    };

    const fmtDate = (ts) => { try { return format(parseUtc(ts), 'MMM d, yyyy HH:mm'); } catch { return ts || ''; } };
    const fmtRel = (ts) => { try { return formatDistanceToNow(parseUtc(ts), { addSuffix: true }); } catch { return ''; } };

    const filteredVouchers = vouchers.filter(v => {
        if (!fSearch) return true;
        const t = fSearch.toLowerCase();
        return v.code?.toLowerCase().includes(t) || v.issued_by_name?.toLowerCase().includes(t) ||
            v.location_name?.toLowerCase().includes(t) || v.staff_payout_name?.toLowerCase().includes(t);
    });

    const totalPages = Math.ceil(vTotal / vLimit);

    return (
        <div className="space-y-6" data-testid="management-audit-page">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <FileText className="w-6 h-6 text-blue-500" />
                    <div>
                        <h1 className="text-2xl font-bold">{t('managementAudit.title')}</h1>
                        <p className="text-sm text-muted-foreground">Voucher activity, staff incentives, and compliance oversight</p>
                    </div>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => activeTab === 'vouchers' ? fetchVouchers() : fetchStaffIncentives()} data-testid="mgmt-refresh">
                        <RefreshCw className="w-4 h-4 mr-1" /> Refresh
                    </Button>
                    <Button variant="outline" size="sm" onClick={handleExport} data-testid="mgmt-export">
                        <Download className="w-4 h-4 mr-1" /> Export CSV
                    </Button>
                </div>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList>
                    <TabsTrigger value="vouchers" data-testid="mgmt-tab-vouchers">
                        <Gift className="w-4 h-4 mr-1" /> Voucher Activity
                    </TabsTrigger>
                    <TabsTrigger value="incentives" data-testid="mgmt-tab-incentives">
                        <TrendingUp className="w-4 h-4 mr-1" /> Staff Incentives
                    </TabsTrigger>
                </TabsList>

                {/* ── VOUCHER ACTIVITY TAB ── */}
                <TabsContent value="vouchers" className="space-y-4">
                    {/* Filters */}
                    <Card>
                        <CardContent className="pt-4">
                            <div className="flex flex-wrap gap-3 items-end">
                                <div className="flex-1 min-w-[200px]">
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                        <Input placeholder="Search code, name, location..." value={fSearch} onChange={e => setFSearch(e.target.value)} className="pl-9" data-testid="mgmt-search" />
                                    </div>
                                </div>
                                <Select value={fColor} onValueChange={v => { setFColor(v); setVPage(0); }}>
                                    <SelectTrigger className="w-[130px]" data-testid="mgmt-filter-color"><SelectValue placeholder={t('managementAudit.color')} /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">All Colors</SelectItem>
                                        <SelectItem value="red">Red</SelectItem>
                                        <SelectItem value="green">Green</SelectItem>
                                        <SelectItem value="gold">Gold</SelectItem>
                                        <SelectItem value="blue">Blue</SelectItem>
                                    </SelectContent>
                                </Select>
                                <Select value={fReason} onValueChange={v => { setFReason(v); setVPage(0); }}>
                                    <SelectTrigger className="w-[150px]" data-testid="mgmt-filter-reason"><SelectValue placeholder={t('managementAudit.reason')} /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">All Reasons</SelectItem>
                                        <SelectItem value="late_food">Late Food</SelectItem>
                                        <SelectItem value="wrong_order">Wrong Order</SelectItem>
                                        <SelectItem value="long_wait">Long Wait</SelectItem>
                                        <SelectItem value="manager_goodwill">Manager Goodwill</SelectItem>
                                        <SelectItem value="birthday">Birthday</SelectItem>
                                        <SelectItem value="vip">VIP</SelectItem>
                                        <SelectItem value="staff_incentive">Staff Incentive</SelectItem>
                                        <SelectItem value="other">Other</SelectItem>
                                    </SelectContent>
                                </Select>
                                <Select value={fStatus} onValueChange={v => { setFStatus(v); setVPage(0); }}>
                                    <SelectTrigger className="w-[130px]" data-testid="mgmt-filter-status"><SelectValue placeholder={t('managementAudit.status')} /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">All Status</SelectItem>
                                        <SelectItem value="active">Active</SelectItem>
                                        <SelectItem value="redeemed">Redeemed</SelectItem>
                                        <SelectItem value="expired">Expired</SelectItem>
                                        <SelectItem value="voided">Voided</SelectItem>
                                    </SelectContent>
                                </Select>
                                <Select value={fLocation} onValueChange={v => { setFLocation(v); setVPage(0); }}>
                                    <SelectTrigger className="w-[150px]" data-testid="mgmt-filter-location"><SelectValue placeholder={t('managementAudit.location')} /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">All Locations</SelectItem>
                                        {locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                                <Input type="date" value={fStartDate} onChange={e => { setFStartDate(e.target.value); setVPage(0); }} className="w-[140px]" data-testid="mgmt-filter-start" />
                                <Input type="date" value={fEndDate} onChange={e => { setFEndDate(e.target.value); setVPage(0); }} className="w-[140px]" data-testid="mgmt-filter-end" />
                            </div>
                        </CardContent>
                    </Card>

                    {/* Voucher Table */}
                    <Card>
                        <CardContent className="pt-4">
                            {vLoading ? (
                                <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" /></div>
                            ) : filteredVouchers.length === 0 ? (
                                <div className="text-center py-12 text-muted-foreground"><Gift className="w-10 h-10 mx-auto mb-2 opacity-30" /><p>{t('managementAudit.noVoucherActivity')}</p></div>
                            ) : (
                                <>
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Date</TableHead>
                                                <TableHead>Code</TableHead>
                                                <TableHead>Color</TableHead>
                                                <TableHead>Reason</TableHead>
                                                <TableHead>Amount</TableHead>
                                                <TableHead>Status</TableHead>
                                                <TableHead>Issued By</TableHead>
                                                <TableHead>Location</TableHead>
                                                <TableHead className="w-10"></TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {filteredVouchers.map(v => (
                                                <TableRow key={v.code} data-testid={`mgmt-row-${v.code}`}>
                                                    <TableCell className="text-xs whitespace-nowrap">
                                                        <div>{fmtDate(v.issued_at)}</div>
                                                        <div className="text-muted-foreground">{fmtRel(v.issued_at)}</div>
                                                    </TableCell>
                                                    <TableCell className="font-mono font-medium text-sm">{v.code}</TableCell>
                                                    <TableCell>
                                                        <Badge className={STRIPE_BADGE[v.stripe_color] || STRIPE_BADGE.green} variant="secondary">
                                                            {v.stripe_color || 'green'}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell className="text-sm">{v.reason_code || '-'}</TableCell>
                                                    <TableCell className="text-sm font-semibold">${v.value}</TableCell>
                                                    <TableCell>
                                                        <Badge className={STATUS_BADGE[v.status] || STATUS_BADGE.active} variant="secondary">
                                                            {v.status}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell className="text-sm">{v.issued_by_name || '-'}</TableCell>
                                                    <TableCell className="text-sm">{v.location_name || '-'}</TableCell>
                                                    <TableCell>
                                                        <Button variant="ghost" size="sm" onClick={() => openDetail(v.code)} data-testid={`mgmt-detail-${v.code}`}>
                                                            <Eye className="w-4 h-4" />
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                    <div className="flex items-center justify-between mt-4 pt-4 border-t">
                                        <p className="text-sm text-muted-foreground">Showing {vPage * vLimit + 1}–{Math.min((vPage + 1) * vLimit, vTotal)} of {vTotal}</p>
                                        <div className="flex gap-2">
                                            <Button variant="outline" size="sm" onClick={() => setVPage(p => p - 1)} disabled={vPage === 0}><ChevronLeft className="w-4 h-4" /></Button>
                                            <span className="flex items-center px-3 text-sm">{vPage + 1} / {totalPages || 1}</span>
                                            <Button variant="outline" size="sm" onClick={() => setVPage(p => p + 1)} disabled={vPage >= totalPages - 1}><ChevronRight className="w-4 h-4" /></Button>
                                        </div>
                                    </div>
                                </>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* ── STAFF INCENTIVES TAB ── */}
                <TabsContent value="incentives" className="space-y-4">
                    <Card>
                        <CardContent className="pt-4">
                            {sLoading ? (
                                <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" /></div>
                            ) : staffList.length === 0 ? (
                                <div className="text-center py-12 text-muted-foreground"><Users className="w-10 h-10 mx-auto mb-2 opacity-30" /><p>{t('managementAudit.noStaffIncentive')}</p></div>
                            ) : (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Staff Member</TableHead>
                                            <TableHead>Total Taps</TableHead>
                                            <TableHead>Current Balance</TableHead>
                                            <TableHead>Total Payouts</TableHead>
                                            <TableHead>Total Paid</TableHead>
                                            <TableHead>Last Payout</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {staffList.map(s => (
                                            <TableRow key={s.employee_id} data-testid={`mgmt-staff-${s.employee_id}`}>
                                                <TableCell className="font-medium">{s.employee_name}</TableCell>
                                                <TableCell>{s.total_taps}</TableCell>
                                                <TableCell className="font-semibold text-blue-600">${(s.incentive_balance || 0).toFixed(2)}</TableCell>
                                                <TableCell>{s.total_payouts}</TableCell>
                                                <TableCell>${(s.total_payout_value || 0).toFixed(2)}</TableCell>
                                                <TableCell className="text-xs text-muted-foreground">
                                                    {s.last_payout_date ? `$${s.last_payout_amount?.toFixed(2)} — ${fmtDate(s.last_payout_date)}` : '-'}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            {/* Voucher Detail Modal */}
            <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
                <DialogContent className="max-w-lg" data-testid="voucher-detail-modal">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Eye className="w-4 h-4" /> Voucher Detail — {detailVoucher?.code}
                        </DialogTitle>
                    </DialogHeader>
                    {detailVoucher && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-3 text-sm">
                                <div><span className="text-muted-foreground">Code:</span> <span className="font-mono font-medium">{detailVoucher.code}</span></div>
                                <div><span className="text-muted-foreground">Amount:</span> <span className="font-semibold">${detailVoucher.value}</span></div>
                                <div><span className="text-muted-foreground">Color:</span> <Badge className={STRIPE_BADGE[detailVoucher.stripe_color] || ''} variant="secondary">{detailVoucher.stripe_color}</Badge></div>
                                <div><span className="text-muted-foreground">Reason:</span> {detailVoucher.reason_code}</div>
                                <div><span className="text-muted-foreground">Status:</span> <Badge className={STATUS_BADGE[detailVoucher.status] || ''} variant="secondary">{detailVoucher.status}</Badge></div>
                                <div><span className="text-muted-foreground">Location:</span> {detailVoucher.location_name || '-'}</div>
                                <div><span className="text-muted-foreground">Issued:</span> {fmtDate(detailVoucher.issued_at)}</div>
                                <div><span className="text-muted-foreground">Issued By:</span> {detailVoucher.issued_by_name}</div>
                                {detailVoucher.redeemed_at && <div><span className="text-muted-foreground">Redeemed:</span> {fmtDate(detailVoucher.redeemed_at)}</div>}
                                {detailVoucher.redeemed_by_name && <div><span className="text-muted-foreground">Redeemed By:</span> {detailVoucher.redeemed_by_name}</div>}
                                <div><span className="text-muted-foreground">Expires:</span> {fmtDate(detailVoucher.expires_at)}</div>
                            </div>

                            {/* Audit Trail */}
                            {detailAudit.length > 0 && (
                                <div>
                                    <h4 className="text-sm font-semibold mb-2 flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Audit Trail</h4>
                                    <div className="space-y-2 max-h-[200px] overflow-y-auto">
                                        {detailAudit.map((a, i) => (
                                            <div key={a.id || `${a.timestamp}-${i}`} className="flex items-start gap-2 text-xs border-l-2 border-blue-300 pl-3 py-1">
                                                <div>
                                                    <span className="font-medium">{a.action}</span>
                                                    <span className="text-muted-foreground ml-2">{fmtDate(a.timestamp)}</span>
                                                    {a.user_email && <span className="text-muted-foreground ml-2">by {a.user_email}</span>}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
